import { type IComplianceTiers } from 'entities/Compliance/lib/complianceTiers'
import { type NavTarget } from 'entities/WebMcp/model/navAreas'

export type ComplianceCategoryKey =
  | 'questionnaire'
  | 'business_profile'
  | 'brand'
  | 'campaign'
  | 'toll_free'
  | 'rcs'

/** One vocabulary for four backend dialects; a raw vendor status never reaches the agent. */
export type ComplianceState =
  | 'not_started'
  | 'in_progress'
  | 'in_review'
  | 'approved'
  | 'action_needed'
  | 'unknown'

export type ComplianceRequirement =
  | 'required'
  | 'recommended'
  | 'optional'
  | 'not_applicable'
  | 'unknown'

/** `actionable` is rejected + not-submitted only: review is the carrier's turn, verified is done. */
export interface NumberBreakdown {
  total: number
  rejected: number
  notSubmitted: number
  inReview: number
  verified: number
  /** Counted so the parts still sum to total. */
  unrecognised: number
  actionable: number
}

export interface ComplianceCategory {
  key: ComplianceCategoryKey
  title: string
  state: ComplianceState
  requirement: ComplianceRequirement
  detail: string
  /** Only meaningful while in review, so it is withheld otherwise. */
  submittedAt: string | null
  navTarget: NavTarget
  numbers?: NumberBreakdown
}

// Per channel, never per account: an org with approved 10DLC keeps sending from its
// local numbers while toll-free is rejected.
export type ChannelKey = 'local' | 'toll_free' | 'rcs'

export type ChannelStatus = 'ready' | 'partial' | 'blocked' | 'not_applicable' | 'unknown'

export type Channels = Record<ChannelKey, ChannelStatus>

export interface ComplianceStatus {
  categories: ComplianceCategory[]
  /** The earliest required category that is not approved, or null if none is. */
  blocker: ComplianceCategoryKey | null
  channels: Channels
  /** True when at least one read failed, so no clean bill of health can be given. */
  unreadable: boolean
}

/** Per-source reads. `null` means the read failed → `unknown`, never guessed. */
export interface ComplianceSignals {
  summary: { questionnaireDone: boolean; tiers: IComplianceTiers } | null
  /** `status: null` with progress saved means started but not yet submitted. */
  businessProfile: {
    status: string | null
    hasProgress: boolean
    submittedAt: string | null
  } | null
  brand: { status: string | null; submittedAt: string | null } | null
  campaign: { status: string | null; submittedAt: string | null } | null
  tollFree: { numberStatuses: string[] } | null
  rcs: { status: string | null } | null
  /** RCS is hidden from the settings menu unless enabled for the organization. */
  rcsAvailable: boolean
}

const DETAIL: Record<ComplianceState, string> = {
  not_started: 'not started',
  in_progress: 'started, not submitted yet',
  in_review: 'submitted, waiting on review',
  approved: 'approved',
  action_needed: 'rejected, needs your attention',
  unknown: 'could not be read',
}

// A value missing from the table is a status the backend added after this build
// shipped: report unknown rather than guess.
const mapStatus = (
  signal: { status: string | null } | null,
  table: Record<string, ComplianceState>,
  whenNoRecord: ComplianceState
): ComplianceState => {
  if (!signal) return 'unknown'
  if (signal.status === null) return whenNoRecord
  return table[signal.status] ?? 'unknown'
}

// Business profile calls carrier review 'under_review' and pending review
// 'in_review'; both are one state to a user who is waiting either way.
const BUSINESS_PROFILE_STATES: Record<string, ComplianceState> = {
  approved: 'approved',
  failed: 'action_needed',
  under_review: 'in_review',
  in_review: 'in_review',
}

const SERVICE_STATES: Record<string, ComplianceState> = {
  approved: 'approved',
  failed: 'action_needed',
  pending_review: 'in_review',
  in_carrier_review: 'in_review',
  in_progress: 'in_progress',
}

// RcsStore renders 'under_review' and 'published' as review states even though the
// response type names neither, so both are mapped rather than falling through.
const RCS_STATES: Record<string, ComplianceState> = {
  approved: 'approved',
  rejected: 'action_needed',
  pending: 'in_review',
  under_review: 'in_review',
  published: 'in_review',
  in_progress: 'in_progress',
}

const TOLL_FREE_STATES: Record<string, ComplianceState> = {
  Verified: 'approved',
  Denied: 'action_needed',
  Blocked: 'action_needed',
  'Under Review': 'in_review',
  'Internal Review': 'in_review',
  Unverified: 'in_progress',
}

const tollFreeNumbers = (signal: ComplianceSignals['tollFree']): NumberBreakdown | undefined => {
  const statuses = signal?.numberStatuses
  if (!statuses?.length) return undefined

  const by = statuses.reduce<Partial<Record<ComplianceState, number>>>((acc, raw) => {
    const state = TOLL_FREE_STATES[raw] ?? 'unknown'
    acc[state] = (acc[state] ?? 0) + 1
    return acc
  }, {})

  const rejected = by.action_needed ?? 0
  const notSubmitted = by.in_progress ?? 0
  return {
    total: statuses.length,
    rejected,
    notSubmitted,
    inReview: by.in_review ?? 0,
    verified: by.approved ?? 0,
    unrecognised: by.unknown ?? 0,
    actionable: rejected + notSubmitted,
  }
}

const tollFreeDetail = (numbers: NumberBreakdown | undefined): string | null => {
  if (!numbers || numbers.total < 2) return null
  const { total, rejected, notSubmitted, inReview, verified, unrecognised, actionable } = numbers

  const waiting = [
    inReview && `${inReview} in review`,
    verified && `${verified} verified`,
    unrecognised && `${unrecognised} this build cannot read`,
  ].filter(Boolean)

  if (!actionable) return `nothing for you to do: ${waiting.join(', ')} of ${total} numbers`

  const todo = [
    rejected && `${rejected} rejected`,
    notSubmitted && `${notSubmitted} not submitted`,
  ].filter(Boolean)

  const rest = waiting.length ? `; ${waiting.join(', ')}` : ''
  return `${actionable} of ${total} numbers need you (${todo.join(', ')})${rest}`
}

// Worst wins, matching the Compliance page's own toll-free icon.
const tollFreeState = (
  signal: ComplianceSignals['tollFree'],
  numbers: NumberBreakdown | undefined
): ComplianceState => {
  if (!signal) return 'unknown'
  if (!numbers) return 'not_started'
  if (numbers.rejected) return 'action_needed'
  // Undecided: every branch below would state a position the unplaced number may contradict.
  if (numbers.unrecognised) return 'unknown'
  if (numbers.verified === numbers.total) return 'approved'
  if (numbers.inReview) return 'in_review'
  return 'in_progress'
}

const toRequirement = (
  tiers: IComplianceTiers | null,
  tab: 'BP' | 'LN' | 'TF'
): ComplianceRequirement => {
  if (!tiers) return 'unknown'
  const tier = tiers[tab]
  if (tier === 'required' || tier === 'recommended' || tier === 'optional') return tier
  return 'not_applicable'
}

// Local is gated by the 10DLC pair, toll-free by its own numbers, RCS by its own
// registration.
const deriveChannels = (
  categories: ReadonlyArray<{ key: ComplianceCategoryKey; state: ComplianceState }>,
  numbers: NumberBreakdown | undefined,
  tiers: IComplianceTiers | null,
  rcsAvailable: boolean
): Channels => {
  const stateOf = (key: ComplianceCategoryKey): ComplianceState =>
    categories.find((c) => c.key === key)?.state ?? 'unknown'

  const local = (): ChannelStatus => {
    const tenDlc = [stateOf('brand'), stateOf('campaign')]
    if (tenDlc.includes('unknown')) return 'unknown'
    if (tenDlc.every((state) => state === 'approved')) return 'ready'
    // `none` means the questionnaire is unanswered: undetermined, not unused.
    if (!tiers || tiers.LN === 'none') return 'unknown'
    return 'blocked'
  }

  const tollFree = (): ChannelStatus => {
    if (stateOf('toll_free') === 'unknown') return 'unknown'
    // Required with no numbers is blocked, not unused, or it contradicts `blocker`.
    if (!numbers) return tiers?.TF === 'required' ? 'blocked' : 'not_applicable'
    if (numbers.verified === numbers.total) return 'ready'
    return numbers.verified > 0 ? 'partial' : 'blocked'
  }

  const rcs = (): ChannelStatus => {
    if (!rcsAvailable) return 'not_applicable'
    const state = stateOf('rcs')
    if (state === 'unknown') return 'unknown'
    return state === 'approved' ? 'ready' : 'blocked'
  }

  return { local: local(), toll_free: tollFree(), rcs: rcs() }
}

export const deriveComplianceStatus = (signals: ComplianceSignals): ComplianceStatus => {
  const { summary, rcsAvailable } = signals
  const tiers = summary?.tiers ?? null
  const numbers = tollFreeNumbers(signals.tollFree)

  /**
   * Prerequisite order, which the blocker walk relies on. The questionnaire leads
   * because Compliance redirects back to it until answered, so a deep link past it
   * silently fails.
   */
  const DEFINITIONS: ReadonlyArray<
    Omit<ComplianceCategory, 'detail'> & { submittedAt: string | null }
  > = [
    {
      key: 'questionnaire',
      title: 'Compliance questionnaire',
      state: !summary ? 'unknown' : summary.questionnaireDone ? 'approved' : 'not_started',
      requirement: summary ? 'required' : 'unknown',
      submittedAt: null,
      // Compliance lands on the questionnaire while it is unanswered.
      navTarget: 'Compliance',
    },
    {
      key: 'business_profile',
      title: 'Business Profile',
      state: mapStatus(
        signals.businessProfile,
        BUSINESS_PROFILE_STATES,
        signals.businessProfile?.hasProgress ? 'in_progress' : 'not_started'
      ),
      requirement: toRequirement(tiers, 'BP'),
      submittedAt: signals.businessProfile?.submittedAt ?? null,
      navTarget: 'Business Profile',
    },
    {
      key: 'brand',
      title: 'Brand',
      state: mapStatus(signals.brand, SERVICE_STATES, 'not_started'),
      requirement: toRequirement(tiers, 'LN'),
      submittedAt: signals.brand?.submittedAt ?? null,
      navTarget: '10DLC Registration',
    },
    {
      key: 'campaign',
      title: 'Campaign',
      state: mapStatus(signals.campaign, SERVICE_STATES, 'not_started'),
      requirement: toRequirement(tiers, 'LN'),
      submittedAt: signals.campaign?.submittedAt ?? null,
      navTarget: '10DLC Registration',
    },
    {
      key: 'toll_free',
      title: 'Toll-Free',
      state: tollFreeState(signals.tollFree, numbers),
      requirement: toRequirement(tiers, 'TF'),
      submittedAt: null,
      navTarget: 'Toll-Free Registration',
      ...(numbers && { numbers }),
    },
    {
      key: 'rcs',
      title: 'RCS',
      state: rcsAvailable ? mapStatus(signals.rcs, RCS_STATES, 'not_started') : 'unknown',
      requirement: rcsAvailable ? 'optional' : 'not_applicable',
      submittedAt: null,
      navTarget: 'RCS',
    },
  ]

  const detailFor = (key: ComplianceCategoryKey, state: ComplianceState): string => {
    if (key === 'rcs' && !rcsAvailable) return 'not available on this account'
    if (key === 'toll_free') return tollFreeDetail(numbers) ?? DETAIL[state]
    return DETAIL[state]
  }

  const categories = DEFINITIONS.map(
    (definition): ComplianceCategory => ({
      ...definition,
      detail: detailFor(definition.key, definition.state),
      submittedAt: definition.state === 'in_review' ? definition.submittedAt : null,
    })
  )

  // Stops at the first unreadable required category: skipping it would name a later
  // step while an earlier prerequisite might still be unfinished.
  const blocker = ((): ComplianceCategoryKey | null => {
    for (const category of categories) {
      if (category.requirement !== 'required') continue
      if (category.state === 'unknown') return null
      if (category.state !== 'approved') return category.key
    }
    return null
  })()

  // RCS switched off is a known absence, not a failed read.
  const unreadable = categories.some(
    (c) => c.state === 'unknown' && !(c.key === 'rcs' && !rcsAvailable)
  )

  return {
    categories,
    blocker,
    channels: deriveChannels(categories, numbers, tiers, rcsAvailable),
    unreadable,
  }
}
